import React from 'react';
import { memo, useEffect, useState, useCallback } from 'react';
import { getDaysInMonth } from 'utils/common';
import { toast } from 'react-toastify';
import { useCreateSalarySlipMutation } from 'redux/features/salaryApi';
import { useNavigate } from 'react-router-dom';
import { useGetPrimaryBankDetailByUserIdQuery } from 'redux/features/bankDetailsApi';

import dayjs from 'lib/dayjs';
import CalculationNote from './CalculationNote';
import Icon from '../../../components/AppIcon';

import Payslip from './Payslip';

const saveSalarySlip = (
  salaryData,
  entries,
  holidayStats,
  bankDetails,
  payDate,
  isApproved = false,
) => {
  const filteredEntries = entries?.map((entry) => ({
    title: entry?.title,
    type: entry?.type?.toUpperCase(),
    tax_type: entry?.taxType,
    tax_value: entry?.taxValue,
    amount: entry?.amount,
    description: entry?.description,
  }));
  return {
    id: Date.now(),
    ...salaryData,
    is_approved: isApproved,
    ...bankDetails,
    calculated_at: payDate ? new Date(payDate).toISOString() : new Date().toISOString(),
    additional_entries: filteredEntries,
    holiday_stats: JSON.stringify(holidayStats),
  };
};

const SalaryBreakdownModal = ({
  salaryData,
  isOpen,
  onClose,
  selectedYear,
  employeeData,
  selectedMonth,
  taxes,
  refetchSalarySlip,
  calculatedSalaryData,
  initialViewMode = 'BREAKDOWN',
  isReadOnlyOnly = false,
  userRole = 'EMPLOYEE',
  defaultPayDate = '',
  isSalaryApproved = false,
}) => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const payslipRef = React.useRef(null);
  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';

  const [submittedWithoutDate, setSubmittedWithoutDate] = useState(false);
  const [isEditingPayDate, setIsEditingPayDate] = useState(false);
  const [shake, setShake] = useState(false);

  const payDateInputRef = React.useRef(null);

  const [bankDetails, setBankDetails] = useState({
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    payment_mode: 'BANK_TRANSFER',
    cheque_no: '',
  });

  const [modalPayDate, setModalPayDate] = useState(() => {
    if (salaryData?.calculated_at) {
      return dayjs(salaryData.calculated_at).format('YYYY-MM-DD');
    }
    if (defaultPayDate) {
      return defaultPayDate;
    }
    return '';
  });

  useEffect(() => {
    if (salaryData?.calculated_at) {
      setModalPayDate(dayjs(salaryData.calculated_at).format('YYYY-MM-DD'));
    } else if (defaultPayDate) {
      setModalPayDate(defaultPayDate);
    } else {
      setModalPayDate('');
    }
  }, [salaryData, defaultPayDate]);

  const [state, setState] = useState({
    salaryData: null,
    entries: [],
    isLoading: false,
    isSaving: false,
    holidayStats: null,
    showConfirmation: false,
    isSlipGenerated: false,
  });

  const navigate = useNavigate();
  const salaryWindow = calculatedSalaryData?.salary_window;

  /* ✅ Fetch Primary Bank Details for new slips */
  const { data: primaryBankData, isLoading: isLoadingPrimaryBank } =
    useGetPrimaryBankDetailByUserIdQuery(employeeData?.id, {
      skip: !!salaryData || !isAdmin || !employeeData?.id,
    });

  useEffect(() => {
    if (!salaryData && primaryBankData) {
      setBankDetails({
        bank_name: primaryBankData.bank_name || '',
        account_holder_name: primaryBankData.account_holder || '',
        account_number: primaryBankData.account_number || '',
        ifsc_code: primaryBankData.ifsc_code || '',
        payment_mode: 'BANK_TRANSFER',
        cheque_no: '',
      });
    }
  }, [primaryBankData, salaryData]);

  useEffect(() => {
    if (isOpen) {
      if (isReadOnlyOnly) {
        setViewMode('BREAKDOWN');
      } else {
        setViewMode(initialViewMode);
      }
      // Reset so entries recalculate on next open
      setState((prev) => ({ ...prev, isSlipGenerated: false }));
    }
  }, [isOpen, initialViewMode, isReadOnlyOnly]);

  /* ✅ Sync props to state */
  useEffect(() => {
    // When slip is NOT approved (draft or new), prioritize real-time calculated data
    // When slip IS approved, use the saved slip data
    const data = isSalaryApproved
      ? salaryData || calculatedSalaryData
      : calculatedSalaryData || salaryData;

    if (data) {
      const totalDays = getDaysInMonth(selectedYear, selectedMonth);
      const standardMinutes = Number(data?.standard_day_minutes) || 540;
      const totalMonthMinute = totalDays * standardMinutes;

      setState((prev) => {
        // Don't overwrite state if slip was just generated in this session
        if (prev.isSlipGenerated) return prev;
        return {
          ...prev,
          salaryData: {
            ...data,
            total_month_minute: totalMonthMinute,
          },
          holidayStats: data?.holiday_stats || null,
        };
      });

      // Pre-fill bank details if they exist on the salary slip (admin/read-only view)
      if (data?.payment_mode) {
        setBankDetails({
          bank_name: data.bank_name || '',
          account_holder_name: data.account_holder_name || '',
          account_number: data.account_number || '',
          ifsc_code: data.ifsc_code || '',
          payment_mode: data.payment_mode || 'BANK_TRANSFER',
          cheque_no: data.cheque_no || '',
        });
      }
    }
  }, [salaryData, calculatedSalaryData, selectedYear, selectedMonth, isSalaryApproved]);

  // Determine if view should be read-only immediately based on having an ID
  const isReadOnly = state.salaryData?.is_approved;
  const saveSalary = useCreateSalarySlipMutation()[0];

  const calculateEmployeeSalary = useCallback(async () => {
    // After slip generation, entries are already correct — don't recompute
    if (state.isSlipGenerated) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      let entries = [];
      const currentSalaryData = state.salaryData || salaryData;

      // Initialize with default tax entries
      entries = isReadOnly
        ? currentSalaryData?.additional_entries?.map((entry) => {
            return {
              id: entry.id,
              title: entry.title,
              type: entry.type == 'ADD' ? 'Add' : 'Subtract',
              amount: entry.amount,
              description: entry.description,
              isEditable: false,
              taxType: entry.tax_type,
              taxValue: entry.tax_value,
            };
          })
        : Number(calculatedSalaryData?.basic_salary) < 15000
          ? []
          : taxes.map((taxEntry) => {
              const amount =
                taxEntry.type === 'PERCENTAGE'
                  ? (Number(calculatedSalaryData.total_salary) * Number(taxEntry.value)) / 100
                  : Number(taxEntry.value);

              return {
                id: taxEntry.id,
                title: taxEntry.title,
                type: 'Subtract',
                amount: Number(amount).toFixed(2),
                description: taxEntry.description,
                isEditable: false, // Make tax entries non-editable
                taxType: taxEntry.type,
                taxValue: Number(taxEntry.value),
              };
            });

      // Add monthly incentives as fixed rupee additions
      if (
        employeeData?.monthlyIncentives &&
        employeeData.monthlyIncentives.length > 0 &&
        !isReadOnly
      ) {
        const incentiveEntries = employeeData.monthlyIncentives
          .filter(
            (incentive) => incentive.title && incentive.amount && Number(incentive.amount) > 0,
          )
          .map((incentive, index) => ({
            id: `incentive_${index}`,
            title: incentive.title,
            type: 'Add',
            amount: Number(incentive.amount).toFixed(2),
            description: 'Monthly incentive',
            isEditable: false, // Make them non-editable
            taxType: 'FIXED', // Fixed rupees only
            taxValue: Number(incentive.amount),
          }));

        entries = [...entries, ...incentiveEntries];
      }

      setState((prev) => ({
        ...prev,
        entries,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Error calculating salary:', error);
      alert('Error calculating salary. Please try again.');
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [
    state.salaryData,
    salaryData,
    calculatedSalaryData,
    taxes,
    employeeData?.monthlyIncentives,
    isReadOnly,
    state.isSlipGenerated,
  ]);

  useEffect(() => {
    if (isOpen && employeeData) {
      calculateEmployeeSalary();
    }
  }, [isOpen, employeeData, selectedYear, selectedMonth, calculateEmployeeSalary]);

  const addNewEntry = () => {
    const newEntry = {
      id: `entry_${Date.now()}`,
      title: '',
      type: 'Add',
      amount: '0',
      description: '',
      isEditable: true,
      taxType: 'FIXED',
      taxValue: 0,
    };
    setState((prev) => ({
      ...prev,
      entries: [...prev.entries, newEntry],
    }));
  };

  const updateEntry = (id, field, value) => {
    setState((prev) => ({
      ...prev,
      entries: prev.entries.map((entry) => {
        if (entry.id === id) {
          const updatedEntry = { ...entry, [field]: value };

          // Recalculate amount for tax entries
          if (field === 'taxValue' || field === 'taxType') {
            const taxType = field === 'taxType' ? value : updatedEntry.taxType;
            const taxValue = field === 'taxValue' ? value : updatedEntry.taxValue;

            const amount =
              taxType === 'PERCENTAGE'
                ? ((Number(prev.salaryData?.monthly_salary) || 0) * Number(taxValue)) / 100
                : Number(taxValue);

            updatedEntry.amount = Number(amount).toFixed(2);
          }

          return updatedEntry;
        }
        return entry;
      }),
    }));
  };

  const removeEntry = (id) => {
    setState((prev) => ({
      ...prev,
      entries: prev.entries.filter((entry) => entry.id !== id),
    }));
  };

  const calculateFixedTotal = () => {
    if (!state.salaryData) return 0;
    const basic = Number(state.salaryData?.basic_salary) || 0;
    const hra = Number(state.salaryData?.house_rent_allowance) || 0;
    const sa = Number(state.salaryData?.special_allowance) || 0;
    const oa = Number(state.salaryData?.other_allowance) || 0;
    const extraLeave = Number(state.salaryData?.extra_leave_salary) || 0;
    const otherLeaves = Number(state.salaryData?.other_leave_salary) || 0;
    const overtime = Number(state.salaryData?.overtime_salary) || 0;
    const paidLeaves = Number(state.salaryData?.paid_leave_salary) || 0;
    // Fixed total = Basic + HRA + SA + OA + Overtime + Paid Leave - Extra Leave - Other Leaves
    return basic + hra + sa + oa + overtime + paidLeaves - extraLeave - otherLeaves;
  };

  const calculateFinalAmount = () => {
    if (!state.salaryData) return 0;

    let finalAmount = calculateFixedTotal();

    state.entries.forEach((entry) => {
      const amount = parseFloat(entry.amount || 0);
      if (entry.type === 'Add') {
        finalAmount += amount;
      } else if (entry.type === 'Subtract') {
        finalAmount -= amount;
      }
    });

    return finalAmount ? finalAmount?.toFixed(2)?.toLocaleString() : '0.00';
  };

  const calculateRunningTotal = (entryIndex) => {
    if (!state.salaryData) return 0;

    let runningTotal = calculateFixedTotal();

    for (let i = 0; i <= entryIndex; i++) {
      const entry = state.entries[i];
      if (entry) {
        const amount = parseFloat(entry.amount || 0);
        if (entry.type === 'Add') {
          runningTotal += amount;
        } else if (entry.type === 'Subtract') {
          runningTotal -= amount;
        }
      }
    }

    return runningTotal;
  };

  const getCalculationFormula = (entry, entryIndex) => {
    // For dynamic entries, the "base" for the first entry is the end of fixed rows
    const fixedTotal = calculateFixedTotal();
    const currentAmount = parseFloat(entry.amount || 0);
    const previousTotal = entryIndex > 0 ? calculateRunningTotal(entryIndex - 1) : fixedTotal;
    const baseCalculatedSalary = Number(state.salaryData?.monthly_salary) || 0;

    if (entry.taxType === 'PERCENTAGE') {
      const PERCENTAGE = entry.taxValue || 0;
      const calculatedAmount = (baseCalculatedSalary * PERCENTAGE) / 100;
      return `${previousTotal.toFixed(2)} ${
        entry.type === 'Add' ? '+' : '-'
      } ${calculatedAmount.toFixed(2)} (${PERCENTAGE}% of ${baseCalculatedSalary.toFixed(
        2,
      )}) = ${calculateRunningTotal(entryIndex).toFixed(2)}`;
    } else {
      return `${previousTotal.toFixed(2)} ${
        entry.type === 'Add' ? '+' : '-'
      } ${currentAmount.toFixed(2)} = ${calculateRunningTotal(entryIndex).toFixed(2)}`;
    }
  };

  const handleApproveClick = () => {
    if (!modalPayDate) {
      payDateInputRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setSubmittedWithoutDate(true);
      setShake(true);
      setTimeout(() => {
        setShake(false);
      }, 500);
      return;
    }
    if (!state.salaryData || state.entries.some((entry) => !entry.title?.trim())) {
      toast.error('Please fill in all required fields before saving.');
      return;
    }
    setState((prev) => ({ ...prev, showConfirmation: true }));
  };

  const handleConfirmSave = async () => {
    setState((prev) => ({ ...prev, isSaving: true }));

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulating slight delay for demo or loading feel

      const savedSlip = saveSalarySlip(
        state.salaryData,
        state.entries,
        state.holidayStats,
        isAdmin ? bankDetails : {},
        modalPayDate,
        true, // is_approved = true when generating
      );

      const res = await saveSalary(savedSlip);

      if (!res.error) {
        toast.success('Salary Slip Generated Successfully');

        // Immediate State Update to Hide Generate Button & Enable ReadOnly
        const savedData = res.data?.data || res.data || savedSlip;

        setState((prev) => ({
          ...prev,
          salaryData: {
            ...prev.salaryData,
            ...savedData,
            id: savedData?.id || savedSlip?.id || prev.salaryData?.id,
            is_approved: savedData?.is_approved ?? true,
            calculated_at: modalPayDate
              ? new Date(modalPayDate).toISOString()
              : prev.salaryData?.calculated_at,
          },
          isSaving: false,
          showConfirmation: false,
          isSlipGenerated: true,
        }));

        // Trigger parent list refresh (asynchronously)
        refetchSalarySlip();
      } else {
        setState((prev) => ({ ...prev, isSaving: false, showConfirmation: false }));
        toast.error('Failed to generate salary slip. Please try again.');
      }
    } catch (error) {
      console.error('Error saving salary slip:', error);
      toast.error('Error saving salary slip. Please try again.');
      setState((prev) => ({ ...prev, isSaving: false, showConfirmation: false }));
    }
  };

  const handleSaveUpdatedPayDate = async () => {
    try {
      const savedSlip = saveSalarySlip(
        state.salaryData,
        state.entries,
        state.holidayStats,
        isAdmin ? bankDetails : {},
        modalPayDate,
        false, // is_approved = false when just saving pay date
      );
      const res = await saveSalary(savedSlip);
      if (!res.error) {
        toast.success('Pay Date Updated Successfully');
      }
      refetchSalarySlip();
    } catch (error) {
      console.error('Error updating pay date:', error);
      toast.error('Error updating pay date.');
    }
  };

  const [showCalculationNote, setShowCalculationNote] = useState(false);

  if (!isOpen) return null;

  if (!state.isLoading && !state.salaryData && viewMode !== 'SLIP') {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-amber-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg text-amber-600">
                <Icon name="AlertTriangle" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Salary Not Configured</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-xl transition-all"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
          <div className="p-6">
            <p className="text-gray-600 text-sm">
              Salary is not configured for{' '}
              <span className="font-bold">
                {employeeData?.first_name} {employeeData?.last_name}
              </span>
              . Would you like to configure it now?
            </p>
          </div>
          <div className="p-5 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onClose();
                navigate(
                  `/employee-management/employee-edit-full-page?id=${employeeData?.id}#salary-info`,
                );
              }}
              className="px-5 py-2 text-sm font-bold text-white bg-amber-600 rounded-xl hover:bg-amber-700 transition-all shadow-sm"
            >
              Configure Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[9999] p-0 sm:p-4 pt-0 sm:pt-10">
      <div className="bg-white rounded-none sm:rounded-lg shadow-xl w-full h-full sm:max-w-6xl sm:max-h-[calc(100vh-6rem)] sm:mt-4 flex flex-col">
        {/* Header */}
        <CalculationNote open={showCalculationNote} onClose={() => setShowCalculationNote(false)} />
        <div className="flex flex-col gap-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-3 sm:p-4 sm:flex-row sm:items-center sm:justify-between">
          {/* Title + subtitle */}
          <div className="min-w-0 flex-1">
            <h2 className="flex items-center gap-2 text-base font-bold text-gray-800 sm:text-lg">
              <span className="inline-flex rounded-lg bg-blue-100 p-1.5">
                <Icon name="Banknote" size={16} className="text-blue-600 sm:h-5 sm:w-5" />
              </span>
              <span className="truncate">
                {viewMode === 'SLIP' ? 'Salary Slip' : 'Salary Breakdown'}
              </span>
            </h2>

            <p className="mt-1 truncate text-sm font-medium text-gray-600">
              {employeeData?.first_name + ' ' + employeeData?.last_name} -{' '}
              {employeeData?.employee_id} |{' '}
              {new Date(selectedYear, selectedMonth - 1)?.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {!isReadOnlyOnly &&
              (viewMode === 'SLIP' ? (
                <>
                  <button
                    onClick={() => setViewMode('BREAKDOWN')}
                    className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white shadow-sm transition-colors hover:bg-blue-700 sm:text-sm"
                  >
                    <Icon name="Eye" size={14} />
                    <span>Salary Breakdown</span>
                  </button>

                  {state.salaryData?.id && (
                    <button
                      onClick={() => payslipRef.current?.handleDownloadPDF()}
                      className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs text-white shadow-sm transition-colors hover:bg-green-700 sm:text-sm"
                    >
                      <Icon name="Download" size={14} />
                      <span>Download</span>
                    </button>
                  )}
                </>
              ) : (
                state.salaryData && (
                  <button
                    onClick={() => setViewMode('SLIP')}
                    className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white shadow-sm transition-colors hover:bg-blue-700 sm:text-sm"
                  >
                    <Icon name="Notebook" size={14} />
                    <span>{state.salaryData?.id ? 'View Slip' : 'Slip Preview'}</span>
                  </button>
                )
              ))}
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="self-start rounded-lg p-2 text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-600"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0 bg-gray-50">
          {viewMode === 'SLIP' ? (
            <Payslip
              ref={payslipRef}
              payslip={{
                ...state.salaryData,
                ...bankDetails,
                calculated_at:
                  state.salaryData?.calculated_at ||
                  (modalPayDate ? new Date(modalPayDate).toISOString() : null),
                additional_entries: state.entries.map((e) => ({
                  ...e,
                  type: e.type.toUpperCase(), // Ensure type is ADD or SUBTRACT for Payslip logic
                })),
              }}
              employee={employeeData}
              hideControls={true}
              isGenerated={!!state.salaryData?.id}
            />
          ) : state.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-gray-600">Calculating salary...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* ONLY SHOW button IF needed (critical for UX) */}

              {/* Salary Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700 font-medium">Basic Salary</p>
                  <p className="text-2xl font-semibold text-blue-900">
                    ₹{(Number(state.salaryData?.basic_salary) || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700 font-medium">Per Minute Rate</p>
                  <p className="text-2xl font-semibold text-blue-900">
                    ₹{(Number(state.salaryData?.salary_per_minute) || 0).toFixed(4)}
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700 font-medium">Total Month Minutes</p>
                  <p className="text-2xl font-semibold text-blue-900">
                    {Math.round(Number(state.salaryData?.total_month_minute) || 0)}m
                  </p>
                </div>
                <div className="bg-emerald-50 sm:col-span-2 p-4 rounded-lg border border-emerald-200">
                  <p className="text-sm text-emerald-700 font-medium tracking-tight">
                    Official Leave Minutes
                  </p>
                  <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-end gap-3 sm:gap-0 mt-1.5">
                    <div className="flex items-baseline">
                      <div className="flex items-center gap-2">
                        <p className="text-2xl font-bold text-emerald-900 leading-none">
                          {Math.round(Number(state?.holidayStats?.totalHolidayMinutes) || 0)}m
                        </p>
                        <p className="text-xs text-emerald-600 mt-1">
                          +₹
                          {(
                            Number(state.salaryData?.holiday_salary).toFixed(2) || 0
                          ).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mb-0.5 flex-wrap">
                      <div className="group relative flex items-center gap-1">
                        <span className="text-emerald-900 font-semibold">
                          {state?.holidayStats?.sundayCount || 0}
                        </span>
                        <Icon
                          name="Sun"
                          size={16}
                          className="text-emerald-600 hover:text-emerald-800 transition-colors cursor-help"
                        />
                        <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 shadow-lg">
                          Sundays
                        </div>
                      </div>
                      <div className="group relative flex items-center gap-1">
                        <span className="text-emerald-900 font-semibold">
                          {state?.holidayStats?.saturdayOffCount || 0}
                        </span>
                        <Icon
                          name="Home"
                          size={16}
                          className="text-emerald-600 hover:text-emerald-800 transition-colors cursor-help"
                        />
                        <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 shadow-lg">
                          Sat Off
                        </div>
                      </div>
                      <div className="group relative flex items-center gap-1">
                        <span className="text-emerald-900 font-semibold">
                          {state?.holidayStats?.fullDayHolidayCount || 0}
                        </span>
                        <Icon
                          name="Calendar"
                          size={16}
                          className="text-emerald-600 hover:text-emerald-800 transition-colors cursor-help"
                        />
                        <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 shadow-lg">
                          Full Day
                        </div>
                      </div>
                      <div className="group relative flex items-center gap-1">
                        <span className="text-emerald-900 font-semibold">
                          {state?.holidayStats?.halfDayHolidayCount || 0}
                        </span>
                        <Icon
                          name="Clock"
                          size={16}
                          className="text-emerald-600 hover:text-emerald-800 transition-colors cursor-help"
                        />
                        <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 shadow-lg">
                          Half Day
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Time Information (Time Parts & Perks) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {/* 1. Time Parts: Worked Minutes */}
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <p className="text-sm text-emerald-700 font-medium">Worked Minutes</p>
                  <div className=" items-center gap-2 mt-1">
                    <p className="text-2xl font-semibold text-emerald-900 leading-none">
                      {Math.round(Number(state.salaryData?.total_regular_minutes))} m
                    </p>
                    <p className="text-xs text-emerald-600 mt-1">
                      +₹
                      {(Number(state.salaryData?.regular_salary).toFixed(2) || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* 2. Time Parts: Extra Leave */}
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700 font-medium">Loss of Pay (LOP)</p>
                  <div className=" items-center gap-2 mt-1">
                    <p className="text-2xl font-semibold text-red-900 leading-none">
                      {Math.round(Number(state.salaryData?.extra_leave_minutes) || 0)}m
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      -₹
                      {(
                        Number(state.salaryData?.extra_leave_salary).toFixed(2) || 0
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* 3. Perks: Overtime */}
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-700 font-medium">Overtime</p>
                  <div className=" items-center gap-2 mt-1">
                    <p className="text-2xl font-semibold text-purple-900 leading-none">
                      {Math.round(Number(state.salaryData?.total_overtime_minutes) || 0)}m
                    </p>
                    <p className="text-xs text-purple-600 mt-1">
                      +₹
                      {(Number(state.salaryData?.overtime_salary).toFixed(2) || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* 4. Perks: Leave Encashment */}
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-700 font-medium">Leave Encashment</p>
                  <div className=" items-center gap-2 mt-1">
                    <p className="text-2xl font-semibold text-purple-900 leading-none">
                      {Math.round(Number(state.salaryData?.paid_leave_minutes) || 0)}m
                    </p>
                    <p className="text-xs text-purple-600 mt-1">
                      +₹
                      {(
                        Number(state.salaryData?.paid_leave_salary).toFixed(2) || 0
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setShowCalculationNote(true)}
                    className="flex items-center gap-1.5 h-9 border border-gray-300 rounded-md px-3 text-sm text-gray-700 bg-white hover:bg-gray-50 flex-shrink-0"
                  >
                    <Icon name={'TriangleAlert'} className="h-4 w-4 text-yellow-600" />
                    How Calculation works
                  </button>

                  {/* First / last salary notice */}
                  {salaryWindow &&
                    (salaryWindow.is_first_salary || salaryWindow.is_last_salary) && (
                      <div className="flex flex-wrap items-center gap-2">
                        {salaryWindow.is_first_salary && (
                          <div className="flex items-center gap-1.5 rounded-md h-9 border border-emerald-200 bg-white px-3 text-sm font-medium text-emerald-700">
                            <Icon name="UserPlus" className="h-4 w-4" />
                            First salary · joined {dayjs(salaryWindow.from).format(
                              'DD MMM YYYY',
                            )} · {salaryWindow.window_days}/{salaryWindow.days_in_month} days
                            payable
                          </div>
                        )}
                        {salaryWindow.is_last_salary && (
                          <div className="flex items-center gap-1.5 rounded-md h-9 border border-amber-200 bg-white px-3 text-sm font-medium text-amber-700">
                            <Icon name="UserMinus" className="h-4 w-4" />
                            Last salary · last day {dayjs(salaryWindow.to).format(
                              'DD MMM YYYY',
                            )} · {salaryWindow.window_days}/{salaryWindow.days_in_month} days
                            payable
                          </div>
                        )}
                      </div>
                    )}
                </div>

                {/* Pay Date UI Section on the right end */}
                <div className="flex items-center gap-3" ref={payDateInputRef}>
                  {isReadOnly && !isEditingPayDate ? (
                    <div className="flex items-center gap-2 h-9 text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 rounded-lg text-sm font-semibold">
                      <Icon name="CalendarCheck" className="h-4 w-4 text-emerald-600" />
                      <span>
                        Pay Date:{' '}
                        {dayjs(modalPayDate || state.salaryData?.calculated_at).format(
                          'DD-MM-YYYY',
                        )}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <div className="flex items-center gap-1.5">
                        {modalPayDate ? (
                          <span className="flex items-center gap-1 h-9 text-emerald-600 text-xs font-semibold bg-emerald-50 px-2 rounded border border-emerald-200">
                            <Icon name="CheckCircle" size={12} /> Ready
                          </span>
                        ) : (
                          <span
                            className={`flex ${shake ? 'shake' : ' '} items-center gap-1 h-9 text-rose-600 text-xs font-semibold bg-rose-50 px-2 rounded border border-rose-200`}
                          >
                            <Icon name="AlertCircle" size={12} /> Required
                          </span>
                        )}
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Pay Date:
                        </label>
                      </div>

                      {!isEditingPayDate && modalPayDate ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-9 items-center px-3 text-sm font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-md">
                            {dayjs(modalPayDate).format('DD-MM-YYYY')}
                          </span>
                          {!state.salaryData?.is_approved && (
                            <button
                              onClick={() => setIsEditingPayDate(true)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md border border-transparent hover:border-blue-200 transition-colors"
                              title="Edit Pay Date"
                            >
                              <Icon name="Pencil" size={14} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            value={modalPayDate}
                            onChange={(e) => {
                              setModalPayDate(e.target.value);
                              if (e.target.value) setSubmittedWithoutDate(false);
                            }}
                            max={dayjs().format('YYYY-MM-DD')}
                            className={`h-9 px-3 text-sm border rounded-md focus:ring-2 outline-none transition-all ${
                              !modalPayDate && submittedWithoutDate
                                ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                : modalPayDate
                                  ? 'border-emerald-300 focus:ring-emerald-500 focus:border-emerald-500 bg-emerald-50/10'
                                  : 'border-rose-300 focus:ring-rose-500 focus:border-rose-500 bg-rose-50/10'
                            }`}
                          />
                          {isEditingPayDate && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => {
                                  if (!modalPayDate) {
                                    toast.error('Pay date is required');
                                    return;
                                  }
                                  setIsEditingPayDate(false);
                                  if (isReadOnly) handleSaveUpdatedPayDate();
                                }}
                                className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md border border-transparent hover:border-emerald-200 transition-colors"
                                title="Save Pay Date"
                              >
                                <Icon name="Check" size={14} />
                              </button>
                              <button
                                onClick={() => {
                                  setIsEditingPayDate(false);
                                  setModalPayDate(
                                    state.salaryData?.calculated_at
                                      ? dayjs(state.salaryData.calculated_at).format('YYYY-MM-DD')
                                      : defaultPayDate,
                                  );
                                }}
                                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md border border-transparent hover:border-gray-200 transition-colors"
                                title="Cancel"
                              >
                                <Icon name="X" size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Breakdown Table - Rest of the component remains exactly the same */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 sm:px-4 py-3">
                  <h3 className="font-medium text-gray-900 flex items-center justify-between">
                    <span>Salary Breakdown</span>
                    {!isReadOnly && (
                      <button
                        onClick={addNewEntry}
                        disabled={isReadOnly}
                        className={`flex items-center space-x-1 px-3 py-1 text-xs sm:text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors w-fit ${
                          isReadOnly ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <Icon name="Plus" size={12} className="sm:w-3.5 sm:h-3.5" />
                        <span>Add Entry</span>
                      </button>
                    )}
                  </h3>
                </div>

                <div className="w-full overflow-x-auto">
                  <div className="flex flex-col min-w-[700px] sm:min-w-[800px]">
                    {/* Scrollable Content */}
                    <div>
                      <div className="divide-y divide-gray-200">
                        {/* Table Header */}
                        <div className="grid grid-cols-10 gap-2 sm:gap-4 p-3 sm:p-4 bg-gray-100 border-b border-gray-200 sticky top-0 z-10">
                          <div className="col-span-3">
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                              Component / Title
                            </span>
                          </div>
                          <div className="col-span-2 text-center sm:text-left">
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                              Type / Tax
                            </span>
                          </div>
                          <div className="col-span-2 text-center sm:text-left">
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                              Amount / Value
                            </span>
                          </div>
                          <div className="col-span-2 text-right sm:text-left">
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                              Subtotal
                            </span>
                          </div>
                          <div className="col-span-1 text-right sm:text-center">
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                              Action
                            </span>
                          </div>
                        </div>

                        {/* Rolling totals for fixed rows */}
                        {(() => {
                          const basic = Number(state.salaryData?.basic_salary) || 0;
                          const hra = Number(state.salaryData?.house_rent_allowance) || 0;
                          const sa = Number(state.salaryData?.special_allowance) || 0;
                          const oa = Number(state.salaryData?.other_allowance) || 0;
                          const overtime = Number(state.salaryData?.overtime_salary) || 0;
                          const paidLeaves = Number(state.salaryData?.paid_leave_salary) || 0;
                          const extraLeave = Number(state.salaryData?.extra_leave_salary) || 0;

                          // Running totals: Additions first, then deductions
                          const total1 = basic;
                          const total2 = total1 + hra;
                          const total3 = total2 + sa;
                          const total4 = total3 + oa;
                          const total5 = total4 + overtime;
                          const total6 = total5 + paidLeaves;
                          const total7 = total6 - extraLeave;

                          // Tooltip style shared across rows
                          const tooltipStyle = {
                            width: '220px',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                          };
                          const tooltipClass =
                            'absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-[99999] shadow-lg';

                          return (
                            <>
                              {/* 1. Basic Salary */}
                              <div className="grid grid-cols-10 gap-2 sm:gap-4 p-3 sm:p-4 bg-gray-25">
                                <div className="col-span-3">
                                  <span className="font-medium text-gray-900">Basic Salary</span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-sm text-gray-600">—</span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-900">
                                    ₹{basic.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-900">
                                    ₹{total1.toFixed(2)}
                                  </span>
                                </div>
                              </div>

                              {/* 2. House Rent Allowance */}
                              <div className="grid grid-cols-10 gap-2 sm:gap-4 p-3 sm:p-4">
                                <div className="col-span-3">
                                  <span className="font-medium text-gray-900">
                                    House Rent Allowance
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-sm text-gray-600">—</span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-900">
                                    ₹{hra.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-900">
                                    ₹{total2.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-1 flex justify-center">
                                  <div className="group relative">
                                    <Icon
                                      name="Info"
                                      size={16}
                                      className="text-gray-400 hover:text-gray-600 cursor-help"
                                    />
                                    <div className={tooltipClass} style={tooltipStyle}>
                                      ₹{total1.toFixed(2)} + ₹{hra.toFixed(2)} = ₹
                                      {total2.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* 3. Special Allowance */}
                              <div className="grid grid-cols-10 gap-2 sm:gap-4 p-3 sm:p-4 bg-gray-25">
                                <div className="col-span-3">
                                  <span className="font-medium text-gray-900">
                                    Special Allowance
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-sm text-gray-600">—</span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-900">
                                    ₹{sa.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-900">
                                    ₹{total3.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-1 flex justify-center">
                                  <div className="group relative">
                                    <Icon
                                      name="Info"
                                      size={16}
                                      className="text-gray-400 hover:text-gray-600 cursor-help"
                                    />
                                    <div className={tooltipClass} style={tooltipStyle}>
                                      ₹{total2.toFixed(2)} + ₹{sa.toFixed(2)} = ₹{total3.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* 4. Other Allowance */}
                              <div className="grid grid-cols-10 gap-2 sm:gap-4 p-3 sm:p-4">
                                <div className="col-span-3">
                                  <span className="font-medium text-gray-900">Other Allowance</span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-sm text-gray-600">—</span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-900">
                                    ₹{oa.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-900">
                                    ₹{total4.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-1 flex justify-center">
                                  <div className="group relative">
                                    <Icon
                                      name="Info"
                                      size={16}
                                      className="text-gray-400 hover:text-gray-600 cursor-help"
                                    />
                                    <div className={tooltipClass} style={tooltipStyle}>
                                      ₹{total3.toFixed(2)} + ₹{oa.toFixed(2)} = ₹{total4.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* 5. Overtime */}
                              <div className="grid grid-cols-10 gap-2 sm:gap-4 p-3 sm:p-4 bg-gray-25">
                                <div className="col-span-3">
                                  <span className="font-medium text-gray-900">Overtime</span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-sm text-gray-600">—</span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-green-700">
                                    +₹{overtime.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-900">
                                    ₹{total5.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-1 flex justify-center">
                                  <div className="group relative">
                                    <Icon
                                      name="Info"
                                      size={16}
                                      className="text-gray-400 hover:text-gray-600 cursor-help"
                                    />
                                    <div className={tooltipClass} style={tooltipStyle}>
                                      ₹{total4.toFixed(2)} + ₹{overtime.toFixed(2)} = ₹
                                      {total5.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* 6. Paid Leave */}
                              <div className="grid grid-cols-10 gap-2 sm:gap-4 p-3 sm:p-4">
                                <div className="col-span-3">
                                  <span className="font-medium text-gray-900">
                                    Leave Encashment
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-sm text-gray-600">—</span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-green-700">
                                    +₹{paidLeaves.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-900">
                                    ₹{total6.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-1 flex justify-center">
                                  <div className="group relative">
                                    <Icon
                                      name="Info"
                                      size={16}
                                      className="text-gray-400 hover:text-gray-600 cursor-help"
                                    />
                                    <div className={tooltipClass} style={tooltipStyle}>
                                      ₹{total5.toFixed(2)} + ₹{paidLeaves.toFixed(2)} = ₹
                                      {total6.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* 7. Extra Leave (Deduction) */}
                              <div className="grid grid-cols-10 gap-2 sm:gap-4 p-3 sm:p-4 bg-red-50">
                                <div className="col-span-3">
                                  <span className="font-medium text-red-600 italic">
                                    Loss of Pay (LOP)
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-sm text-gray-600">—</span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-red-600">
                                    -₹{extraLeave.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-bold text-gray-900">
                                    ₹{total7.toFixed(2)}
                                  </span>
                                </div>
                                <div className="col-span-1 flex justify-center">
                                  <div className="group relative">
                                    <Icon
                                      name="Info"
                                      size={16}
                                      className="text-gray-400 hover:text-gray-600 cursor-help"
                                    />
                                    <div className={tooltipClass} style={tooltipStyle}>
                                      ₹{total6.toFixed(2)} - ₹{extraLeave.toFixed(2)} = ₹
                                      {total7.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        })()}

                        {/* Dynamic Entries */}
                        {state.entries.map((entry, entryIndex) => (
                          <div
                            key={entry?.id}
                            className="grid grid-cols-10 gap-2 sm:gap-4 p-3 sm:p-4"
                          >
                            <div className="col-span-3">
                              <input
                                type="text"
                                value={entry?.title || ''}
                                onChange={(e) => updateEntry(entry?.id, 'title', e?.target?.value)}
                                placeholder="Name of the entry"
                                className={`w-full px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  entry?.isIncentive || entry?.isTax
                                    ? 'bg-gray-50 text-gray-700'
                                    : ''
                                }`}
                                disabled={!entry?.isEditable || isReadOnly}
                              />
                            </div>
                            <div className="col-span-2">
                              <div className="flex space-x-2">
                                <select
                                  value={entry?.type}
                                  onChange={(e) => updateEntry(entry?.id, 'type', e?.target?.value)}
                                  className={`flex-1 px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                    entry?.isIncentive || entry?.isTax
                                      ? 'bg-gray-50 text-gray-700'
                                      : ''
                                  }`}
                                  disabled={!entry?.isEditable || isReadOnly}
                                >
                                  <option value="Add">+</option>
                                  <option value="Subtract">-</option>
                                </select>
                                <select
                                  value={entry?.taxType || 'FIXED'}
                                  onChange={(e) =>
                                    updateEntry(entry?.id, 'taxType', e?.target?.value)
                                  }
                                  className={`flex-1 px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                    entry?.isIncentive || entry?.isTax
                                      ? 'bg-gray-50 text-gray-700'
                                      : ''
                                  }`}
                                  disabled={
                                    entry?.isIncentive ||
                                    entry?.isTax ||
                                    !entry?.isEditable ||
                                    isReadOnly
                                  }
                                >
                                  <option value="FIXED">₹</option>
                                  <option value="PERCENTAGE">%</option>
                                </select>
                              </div>
                            </div>
                            <div className="col-span-2">
                              <div className="flex flex-col space-y-1">
                                {(entry?.taxType || 'FIXED') === 'PERCENTAGE' ? (
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="number"
                                      value={entry?.taxValue}
                                      onChange={(e) =>
                                        updateEntry(
                                          entry?.id,
                                          'taxValue',
                                          parseFloat(e?.target?.value) || 0,
                                        )
                                      }
                                      min="0"
                                      max="50"
                                      className={`w-16 px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                        entry?.isIncentive || entry?.isTax
                                          ? 'bg-gray-50 text-gray-700'
                                          : ''
                                      }`}
                                      disabled={
                                        entry?.isIncentive ||
                                        entry?.isTax ||
                                        !entry?.isEditable ||
                                        isReadOnly
                                      }
                                    />
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="number"
                                      value={entry?.taxValue}
                                      onChange={(e) =>
                                        updateEntry(
                                          entry?.id,
                                          'taxValue',
                                          parseFloat(e?.target?.value) || 0,
                                        )
                                      }
                                      min="0"
                                      step="0.01"
                                      className={`w-20 px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                        entry?.isIncentive || entry?.isTax
                                          ? 'bg-gray-50 text-gray-700'
                                          : ''
                                      }`}
                                      disabled={
                                        entry?.isIncentive ||
                                        entry?.isTax ||
                                        !entry?.isEditable ||
                                        isReadOnly
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="col-span-2">
                              <div className="flex flex-col space-y-1">
                                <span className="font-medium text-gray-900">₹{entry?.amount}</span>
                                <span className="text-sm text-gray-500 font-semibold">
                                  ₹{calculateRunningTotal(entryIndex)?.toFixed(2)?.toLocaleString()}
                                </span>
                              </div>
                            </div>
                            <div className="col-span-1">
                              <div className="flex items-end space-x-2">
                                <div className="group relative">
                                  <Icon
                                    name="Info"
                                    size={16}
                                    className="text-gray-400 hover:text-gray-600 cursor-help"
                                  />
                                  <div
                                    className="absolute top-full right-0 mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-[99999] shadow-lg"
                                    style={{
                                      width: '200px',
                                      wordWrap: 'break-word',
                                      whiteSpace: 'normal',
                                    }}
                                  >
                                    {getCalculationFormula(entry, entryIndex)}
                                  </div>
                                </div>
                                {entry?.id !== 'tax_default' && entry?.id !== 'pf' && (
                                  <button
                                    onClick={() => removeEntry(entry?.id)}
                                    className="text-red-600 hover:text-red-800 p-1"
                                    title="Remove Entry"
                                    disabled={isReadOnly}
                                    hidden={isReadOnly}
                                  >
                                    <Icon name="Trash2" size={14} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Fixed Final Amount Row */}
                    <div className="border-t border-gray-200 bg-gray-100">
                      <div className="grid grid-cols-10 gap-2 sm:gap-4 p-3 sm:p-4 font-semibold">
                        <div className="col-span-3">
                          <span className="text-gray-900">Final Amount</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-sm text-gray-600">—</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-sm text-gray-600">—</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-lg text-green-700">₹{calculateFinalAmount()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== Bank Details Section (Admin/HR Only) ===== */}
              {isAdmin && (
                <div className="border border-indigo-200 rounded-lg overflow-hidden">
                  <div className="bg-indigo-50 px-3 sm:px-4 py-3 flex items-center space-x-2">
                    <Icon name="CreditCard" size={16} className="text-indigo-600" />
                    <h3 className="font-medium text-indigo-900">Payment Details</h3>
                    {isReadOnly && (
                      <span className="text-xs text-indigo-500 ml-auto">(Read-only)</span>
                    )}
                  </div>

                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 bg-white">
                    {!primaryBankData && !salaryData && !isLoadingPrimaryBank ? (
                      <div className="col-span-full py-6 flex flex-col items-center justify-center bg-amber-50 rounded-lg border border-dashed border-amber-300">
                        <Icon name="AlertTriangle" size={32} className="text-amber-500 mb-2" />
                        <p className="text-sm font-medium text-amber-800">
                          No bank details configured
                        </p>
                        <p className="text-xs text-amber-600 mb-4">
                          Please set up bank details before generating a slip.
                        </p>
                        <button
                          onClick={() =>
                            navigate(
                              `/employee-management/employee-edit-full-page?id=${employeeData?.id}#bank-info`,
                            )
                          }
                          className="px-4 py-2 bg-amber-600 text-white text-xs font-semibold rounded-md hover:bg-amber-700 transition-colors flex items-center space-x-2"
                        >
                          <Icon name="ExternalLink" size={14} />
                          <span>Configure Bank Details</span>
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* Bank Name */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Bank Name
                          </label>
                          <input
                            type="text"
                            value={bankDetails.bank_name}
                            onChange={(e) =>
                              setBankDetails((prev) => ({ ...prev, bank_name: e.target.value }))
                            }
                            disabled={isReadOnly}
                            placeholder="e.g. State Bank of India"
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                          />
                        </div>

                        {/* Account Holder Name */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Account Holder Name
                          </label>
                          <input
                            type="text"
                            value={bankDetails.account_holder_name}
                            onChange={(e) =>
                              setBankDetails((prev) => ({
                                ...prev,
                                account_holder_name: e.target.value,
                              }))
                            }
                            disabled={isReadOnly}
                            placeholder="Full legal name"
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                          />
                        </div>

                        {/* Payment Mode */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Payment Mode
                          </label>
                          <select
                            value={bankDetails.payment_mode}
                            onChange={(e) =>
                              setBankDetails((prev) => ({
                                ...prev,
                                payment_mode: e.target.value,
                                cheque_no: '',
                              }))
                            }
                            disabled={isReadOnly}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                          >
                            <option value="BANK_TRANSFER">Bank Transfer</option>
                            <option value="CHEQUE">Cheque</option>
                            <option value="CASH">Cash</option>
                          </select>
                        </div>

                        {/* Account Number — only for Bank Transfer or Cheque */}
                        {bankDetails.payment_mode !== 'CASH' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Account Number
                            </label>
                            <input
                              type="text"
                              value={bankDetails.account_number}
                              onChange={(e) =>
                                setBankDetails((prev) => ({
                                  ...prev,
                                  account_number: e.target.value,
                                }))
                              }
                              disabled={isReadOnly}
                              placeholder="Account number (encrypted)"
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                            />
                          </div>
                        )}

                        {/* IFSC Code — only for Bank Transfer */}
                        {bankDetails.payment_mode === 'BANK_TRANSFER' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              IFSC Code
                            </label>
                            <input
                              type="text"
                              value={bankDetails.ifsc_code}
                              onChange={(e) =>
                                setBankDetails((prev) => ({
                                  ...prev,
                                  ifsc_code: e.target.value.toUpperCase(),
                                }))
                              }
                              disabled={isReadOnly}
                              placeholder="e.g. SBIN0001234"
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                            />
                          </div>
                        )}

                        {/* Cheque Number — only when payment mode is CHEQUE */}
                        {bankDetails.payment_mode === 'CHEQUE' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Cheque Number
                            </label>
                            <input
                              type="text"
                              value={bankDetails.cheque_no}
                              onChange={(e) =>
                                setBankDetails((prev) => ({ ...prev, cheque_no: e.target.value }))
                              }
                              disabled={isReadOnly}
                              placeholder="Cheque number (encrypted)"
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {state.salaryData && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 border-t border-gray-200 space-y-3 sm:space-y-0 flex-shrink-0">
            <div className="text-xs sm:text-sm text-gray-500 space-y-1">
              {state.salaryData.calculated_at && (
                <div>{`Calculated at ${new Date(state.salaryData.calculated_at).toLocaleString()}`}</div>
              )}
              {!isReadOnly && salaryWindow && !salaryWindow.has_end_date && (
                <div>
                  Is this the employee&apos;s last salary?{' '}
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/employee-management/employee-edit-full-page?id=${employeeData?.id}#employee-periods-section`,
                      )
                    }
                    className="font-semibold text-primary underline hover:text-primary-700"
                  >
                    Set the period end date
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                Cancel
              </button>
              {/* Generate Button Logic: Hidden if Is ReadOnly or Salary Data Has ID */}
              {!isReadOnly && (
                <button
                  onClick={handleApproveClick}
                  disabled={state.isSaving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2 text-sm"
                >
                  {state.isSaving && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  <span>Generate</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {state.showConfirmation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Confirm Payslip Generation</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Please review the payslip preview below before generating.
                </p>
              </div>
              <button
                onClick={() => setState((prev) => ({ ...prev, showConfirmation: false }))}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-xl transition-all"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 pointer-events-none">
                <Payslip
                  payslip={{
                    ...state.salaryData,
                    ...bankDetails,
                    calculated_at: modalPayDate ? new Date(modalPayDate).toISOString() : null,
                    additional_entries: state.entries.map((e) => ({
                      ...e,
                      type: e.type.toUpperCase(),
                    })),
                  }}
                  employee={employeeData}
                  hideControls={true}
                  isGenerated={false}
                />
              </div>
            </div>

            <div className="p-5 border-t border-gray-100 bg-white flex items-center justify-end gap-3">
              <button
                onClick={() => setState((prev) => ({ ...prev, showConfirmation: false }))}
                disabled={state.isSaving}
                className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSave}
                disabled={state.isSaving}
                className="px-5 py-2.5 text-sm font-bold text-white bg-green-600 rounded-xl hover:bg-green-700 transition-all shadow-sm flex items-center gap-2 disabled:opacity-50"
              >
                {state.isSaving ? (
                  <>
                    <Icon name="Loader" size={16} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Icon name="CheckCircle" size={16} />
                    Generate Slip
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(SalaryBreakdownModal);
